/**
 * CommissionRulesPage — Issue #372.
 *
 * Read + write list for multi-stage commission rules. Activation and
 * archival happen via inline buttons that consult the same typed client.
 * 没有“假状态”：每次写完都触发一次后端拉取。
 */
import { useEffect, useMemo, useState } from 'react';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  FINANCE_PERMISSIONS,
  hasPermission,
} from '@/lib/financePermissions';
import {
  useCommissionRules,
  type CommissionRule,
  type CreateCommissionRule,
} from '@/hooks/useCommission372';

const ROLE_OPTIONS: ReadonlyArray<{
  value: NonNullable<CreateCommissionRule['roleType']>;
  label: string;
}> = [
  { value: 'development', label: '开发' },
  { value: 'follow_up', label: '跟进' },
  { value: 'lead_close', label: '主导压单' },
  { value: 'assistant_close', label: '辅助压单' },
  { value: 'base_service', label: '基础服务' },
  { value: 'solution_writing', label: '方案撰写' },
];

const PROJECT_TYPE_OPTIONS: ReadonlyArray<{
  value: NonNullable<CreateCommissionRule['projectType']>;
  label: string;
}> = [
  { value: 'default', label: '默认' },
  { value: 'self_operated_core', label: '自营核心' },
  { value: 'cooperative', label: '合作' },
  { value: 'channel', label: '渠道' },
];

const BASE_OPTIONS: ReadonlyArray<{
  value: NonNullable<CreateCommissionRule['baseAmountType']>;
  label: string;
}> = [
  { value: 'project_contract', label: '项目对价（基数）' },
  { value: 'confirmed_payment', label: '已确认回款' },
  { value: 'revenue_net', label: '收益净额' },
];

function fmtPct(rate: string): string {
  try {
    return `${new Decimal(rate).mul(100).toFixed(2)}%`;
  } catch {
    return rate;
  }
}

export default function CommissionRulesPage() {
  const { permissions } = useAuth();
  const canRead = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_RULE_READ,
  );
  const canWrite = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_RULE_WRITE,
  );
  const canActivate = hasPermission(
    permissions,
    FINANCE_PERMISSIONS.COMMISSION_RULE_ACTIVATE,
  );

  const { rules, loading, error, fetchRules, createRule, updateRuleStatus } =
    useCommissionRules();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<CreateCommissionRule>({
    ruleCode: '',
    projectType: 'default',
    roleType: 'development',
    rateDecimal: '0.05',
    baseAmountType: 'project_contract',
    effectiveFrom: new Date().toISOString().slice(0, 10) + 'T00:00:00Z',
    effectiveTo: null,
    notes: null,
  });

  useEffect(() => {
    if (canRead) void fetchRules();
  }, [canRead, fetchRules]);

  const byCode = useMemo(() => {
    const map = new Map<string, CommissionRule[]>();
    for (const r of rules) {
      const bucket = map.get(r.ruleCode) ?? [];
      bucket.push(r);
      map.set(r.ruleCode, bucket);
    }
    return map;
  }, [rules]);

  if (!canRead) {
    return (
      <div className="flex items-center justify-center py-24 text-sm text-muted-foreground">
        您没有查看提成规则的权限。
      </div>
    );
  }

  async function handleCreate(event: React.FormEvent) {
    event.preventDefault();
    const created = await createRule(form);
    if (created) {
      setShowForm(false);
      void fetchRules();
    }
  }

  async function handleActivate(rule: CommissionRule) {
    if (!confirm(`激活 ${rule.ruleCode} v${rule.version}？`)) return;
    const updated = await updateRuleStatus(rule.id, {
      expectedRecordVersion: rule.recordVersion,
      status: 'active',
    });
    if (updated) void fetchRules();
  }

  async function handleArchive(rule: CommissionRule) {
    if (!confirm(`归档 ${rule.ruleCode} v${rule.version}？`)) return;
    const updated = await updateRuleStatus(rule.id, {
      expectedRecordVersion: rule.recordVersion,
      status: 'archived',
    });
    if (updated) void fetchRules();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">提成规则 (#372)</h1>
        {canWrite && (
          <button
            onClick={() => setShowForm((v) => !v)}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            {showForm ? '取消' : '新增规则草稿'}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error.message}
        </div>
      )}

      {showForm && canWrite && (
        <form
          onSubmit={handleCreate}
          className="rounded border bg-white p-4 grid grid-cols-2 gap-3 text-sm"
        >
          <label className="flex flex-col">
            规则代号
            <input
              required
              className="rounded border border-gray-300 px-2 py-1"
              value={form.ruleCode}
              onChange={(e) => setForm({ ...form, ruleCode: e.target.value })}
            />
          </label>
          <label className="flex flex-col">
            项目类型
            <select
              className="rounded border border-gray-300 px-2 py-1"
              value={form.projectType}
              onChange={(e) =>
                setForm({
                  ...form,
                  projectType: e.target
                    .value as CreateCommissionRule['projectType'],
                })
              }
            >
              {PROJECT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col">
            角色
            <select
              className="rounded border border-gray-300 px-2 py-1"
              value={form.roleType}
              onChange={(e) =>
                setForm({
                  ...form,
                  roleType: e.target.value as CreateCommissionRule['roleType'],
                })
              }
            >
              {ROLE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col">
            基数口径
            <select
              className="rounded border border-gray-300 px-2 py-1"
              value={form.baseAmountType}
              onChange={(e) =>
                setForm({
                  ...form,
                  baseAmountType: e.target
                    .value as CreateCommissionRule['baseAmountType'],
                })
              }
            >
              {BASE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col">
            提成比例（0-1 decimal）
            <input
              required
              className="rounded border border-gray-300 px-2 py-1"
              value={form.rateDecimal}
              onChange={(e) =>
                setForm({ ...form, rateDecimal: e.target.value })
              }
            />
          </label>
          <label className="flex flex-col">
            生效起始
            <input
              required
              type="datetime-local"
              className="rounded border border-gray-300 px-2 py-1"
              value={form.effectiveFrom.slice(0, 16)}
              onChange={(e) =>
                setForm({
                  ...form,
                  effectiveFrom: new Date(e.target.value).toISOString(),
                })
              }
            />
          </label>
          <label className="flex flex-col">
            生效截止（可选）
            <input
              type="datetime-local"
              className="rounded border border-gray-300 px-2 py-1"
              value={form.effectiveTo ? form.effectiveTo.slice(0, 16) : ''}
              onChange={(e) =>
                setForm({
                  ...form,
                  effectiveTo: e.target.value
                    ? new Date(e.target.value).toISOString()
                    : null,
                })
              }
            />
          </label>
          <label className="col-span-2 flex flex-col">
            备注
            <textarea
              className="rounded border border-gray-300 px-2 py-1"
              rows={2}
              value={form.notes ?? ''}
              onChange={(e) =>
                setForm({ ...form, notes: e.target.value || null })
              }
            />
          </label>
          <div className="col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={loading}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              保存草稿
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-muted-foreground">
          加载中…
        </div>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left text-gray-500">规则代号</th>
                <th className="px-3 py-2 text-left text-gray-500">项目</th>
                <th className="px-3 py-2 text-left text-gray-500">角色</th>
                <th className="px-3 py-2 text-left text-gray-500">基数口径</th>
                <th className="px-3 py-2 text-left text-gray-500">比例</th>
                <th className="px-3 py-2 text-left text-gray-500">版本</th>
                <th className="px-3 py-2 text-left text-gray-500">状态</th>
                <th className="px-3 py-2 text-left text-gray-500">生效</th>
                <th className="px-3 py-2 text-right text-gray-500">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rules.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-3 py-6 text-center text-muted-foreground"
                  >
                    暂无规则
                  </td>
                </tr>
              ) : (
                [...byCode.entries()].flatMap(([code, group]) =>
                  group.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50">
                      <td className="px-3 py-2 font-mono">{code}</td>
                      <td className="px-3 py-2">{r.projectType}</td>
                      <td className="px-3 py-2">{r.roleType}</td>
                      <td className="px-3 py-2">{r.baseAmountType}</td>
                      <td className="px-3 py-2 font-semibold">
                        {fmtPct(r.rateDecimal)}
                      </td>
                      <td className="px-3 py-2">v{r.version}</td>
                      <td className="px-3 py-2">{r.status}</td>
                      <td className="px-3 py-2">
                        {r.effectiveFrom.slice(0, 10)}
                        {r.effectiveTo ? ` ~ ${r.effectiveTo.slice(0, 10)}` : ''}
                      </td>
                      <td className="px-3 py-2 text-right space-x-2">
                        {canActivate && r.status === 'draft' && (
                          <button
                            className="rounded bg-green-600 px-2 py-0.5 text-xs text-white"
                            onClick={() => {
                              void handleActivate(r);
                            }}
                          >
                            激活
                          </button>
                        )}
                        {canActivate && r.status !== 'archived' && (
                          <button
                            className="rounded bg-gray-500 px-2 py-0.5 text-xs text-white"
                            onClick={() => {
                              void handleArchive(r);
                            }}
                          >
                            归档
                          </button>
                        )}
                      </td>
                    </tr>
                  )),
                )
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
